import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  ComboModal,
  ComboModalItem,
  Loader,
  Option,
  RadioButton,
  RadioButtonGroup,
  TextInput,
  Tooltip,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Parameter, Product, ProductOffer, ProductPageValidation } from 'src/front/api/apiInstance';
import {
  CardListData,
  CardSelectionType,
  CardUsageType,
  PageOperationType,
  ProductComboType,
  ReferenceDataType,
  TransferableCardData,
} from 'src/front/api/generated';
import infoIcon from 'src/front/assets/icon_info.svg';
import useDeviceType from 'src/front/common/hooks/useDeviceType';
import usePRM from 'src/front/common/hooks/usePRM';
import { scrollTo } from 'src/front/common/utils/domUtils';
import { dictionaryLineFormat, optionFormat } from 'src/front/common/utils/formUtils';
import { ProductInformationSchema } from 'src/front/common/utils/validationSchemas';
import { SearchFormData } from 'src/front/components//CustomerSearchForm';
import Markdown from 'src/front/components//Markdown';

import {
  CheckboxElement,
  Container,
  Form,
  FormElement,
  FormRow,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { BREAK_POINTS } from 'src/front/constant';
import {
  useCardTypeDispatch,
  useProductOfferDispatch,
  useProductOfferState,
} from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

import MainCardSelect from './MainCardSelect';

const LineFormElement = styled(FormElement)`
  width: 685px;

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) and (min-width: ${BREAK_POINTS.MIN
      .PHONE}) {
    width: 520px;
  }
`;

const MainCardSelector = styled(FormElement)`
  display: flex;
`;

const RadioButtonContainer = styled(FormElement)`
  display: flex;
  align-items: center;

  height: 60px;
`;
const CheckboxContainer = styled(FormElement)`
  display: flex;
  align-items: center;
  justify-content: start;

  height: 60px;
`;
const TooltipContainer = styled.div`
  margin-left: 5px;
  margin-top: -5px;
`;

const MainCardInput = styled(TextInput)`
  width: 70%;

  & input {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
  }
`;

const MainCardButton = styled(Button)`
  width: 30%;
  height: 60px;

  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
`;

const NoCardText = styled.div`
  color: ${({ theme }) => theme.palette.deepSeaBlue};

  text-align: center;
`;

// TODO(emre.yildirim): Move this change to Radio Button component
const StyledRadioButton = styled(RadioButton)`
  &:last-of-type {
    margin-right: 0;
  }
`;

const PRODUCT_DETAILS = {
  [ProductComboType.CUSTOMER]: 0,
  [ProductComboType.CUSTOMERSUB]: 1,
  [ProductComboType.DEBITFLAG]: 2,
  [ProductComboType.RESIDENT]: 3,
  [ProductComboType.ACCOUNT]: 4,
  [ProductComboType.BRANDOWNER]: 5,
  [ProductComboType.CARD]: 6,
};

const PRODUCT_DETAILS_RESET = [
  { name: 'customerType', value: null },
  { name: 'customerSubType', value: null },
  { name: 'debitFlag', value: null },
  { name: 'resident', value: null },
  { name: 'accountType', value: null },
  { name: 'brandOwner', value: null },
  { name: 'productCardType', value: null },
];

interface CardComboModalItem extends ComboModalItem {
  brandOwnerTypeCode: string;
  cardTypeCode: string;
}

interface FormData {
  pos: Option | null;
  subPos: Option | null;
  cardType: Option | null;
  printType: Option | null;
  cardSelectionType: CardSelectionType;
  cardUsageType: CardUsageType;
  mainCard: CardListData | null;
  card: CardComboModalItem | null;
  customerType: Option | null;
  customerSubType: Option | null;
  debitFlag: Option | null;
  resident: Option | null;
  accountType: Option | null;
  brandOwner: Option | null;
  productCardType: Option | null;
  isSalaryClient: boolean;
  isProductTransfer: boolean;
  isSignDocuments: boolean;
  isProductOffer: boolean;
  transferableCardInfo: TransferableCardData | null;
}

const defaultValues = {
  pos: null,
  subPos: null,
  cardType: null,
  printType: null,
  cardSelectionType: CardSelectionType.DEFAULT,
  cardUsageType: CardUsageType.PRIMARY,
  mainCard: null,
  card: null,
  customerType: null,
  customerSubType: null,
  debitFlag: null,
  resident: null,
  accountType: null,
  brandOwner: null,
  productCardType: null,
  isSalaryClient: false,
  isProductTransfer: false,
  isSignDocuments: false,
  isProductOffer: false,
  transferableCardInfo: null,
};

interface Props {
  goToNextStep: () => void;
  isEdit?: boolean;
  isProductOfferFlow?: boolean;
  disablePage?: boolean;
}

const ProductInformation: FC<Props> = ({
  goToNextStep,
  isEdit = false,
  isProductOfferFlow = false,
  disablePage,
}) => {
  const { t } = useTranslation();
  const { isTablet } = useDeviceType();
  const dispatchCardType = useCardTypeDispatch();
  const dispatchProductOffer = useProductOfferDispatch();
  const productOfferState = useProductOfferState();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isComboParameterLoading, setIsComboParametersLoading] = useState<boolean>(false);

  const [mainPointOfSales, setMainPointOfSales] = useState<Option[]>([]);
  const [subPointOfSales, setSubPointOfSales] = useState<Option[]>([]);
  const [cards, setCards] = useState<CardComboModalItem[]>([]);
  const [customerTypes, setCustomerTypes] = useState<Option[]>([]);
  const [customerSubTypes, setCustomerSubTypes] = useState<Option[]>([]);
  const [debitFlags, setDebitFlags] = useState<Option[]>([]);
  const [residents, setResidents] = useState<Option[]>([]);
  const [accountTypes, setAccountTypes] = useState<Option[]>([]);
  const [brandOwners, setBrandOwners] = useState<Option[]>([]);
  const [productCardTypes, setProductCardTypes] = useState<Option[]>([]);
  const [isSalaryVisible, setSalaryVisible] = useState<boolean>(false);
  const [productOfferVisible, setProductOfferVisible] = useState<boolean>(false);
  const [productOfferChecked, setProductOfferChecked] = useState<boolean>(false);
  const [cardTypeVisible, setCardTypeVisible] = useState<boolean>(true);
  const [cardTypeUsageEnabled, setCardTypeUsageEnabled] = useState<boolean>(true);
  const [showCardModal, setShowCardModal] = useState<boolean>(false);
  const [cardModalFormValues, setCardModalFormValues] = useState<SearchFormData>();

  const toggleModal = (): void => setShowCardModal((state) => !state);

  const productDetailPRMSetters = useMemo(
    () => [
      setCustomerTypes,
      setCustomerSubTypes,
      setDebitFlags,
      setResidents,
      setAccountTypes,
      setBrandOwners,
      setProductCardTypes,
    ],
    []
  );

  const isDisabled = isEdit || disablePage;
  const cardTypes = usePRM(ReferenceDataType.CARDSELECTIONTYPE);
  const cardPrintTypes = usePRM(ReferenceDataType.CARDPRINTTYPE);

  const { handleSubmit, errors, register, setValue, watch, getValues, formState } = useForm<
    FormData
  >({
    defaultValues,
    resolver: yupResolver(ProductInformationSchema),
  });

  const getCards = useCallback(
    async (
      cardType: Option | null,
      printType: Option | null,
      subPos: Option | null
    ): Promise<CardComboModalItem[]> => {
      if (cardType && printType && subPos) {
        const { data } = await Product.getCardImageDetails({
          fillPointId: subPos?.value || '',
          cardSelectionType:
            cardType?.value === 'I' ? CardSelectionType.CREDITCARD : CardSelectionType.DEBIT,
          printType: printType?.value || '',
        });
        const cardList = data.cardList.map((card) => ({
          id: card.cardImageId,
          name: card.cardName,
          description: card.cardInformation,
          image: card.cardImagePath,
          brandOwnerTypeCode: card.brandOwnerTypeCode,
          cardTypeCode: card.cardTypeCode,
        }));
        setCards(cardList);

        return cardList;
      }
      return [];
    },
    []
  );

  const getProductComboItems = useCallback(
    (type?: ProductComboType) => async (item: Option | null) => {
      let detailIndex = -1;

      if (type) {
        detailIndex = PRODUCT_DETAILS[type];
        productDetailPRMSetters.slice(detailIndex).forEach((setter) => setter([]));

        PRODUCT_DETAILS_RESET.slice(detailIndex).forEach(({ name, value }) =>
          setValue(name, (value as unknown) as {} | undefined)
        );
      }
      const {
        card,
        subPos,
        customerType,
        customerSubType,
        debitFlag,
        resident,
        accountType,
        brandOwner,
        cardType,
      } = getValues();
      if (type === ProductComboType.CUSTOMER) {
        const { data } = await Product.getProductCombo({
          productComboType: type,
          fillPointId: subPos?.value || '',
          cardTypeCode: card?.cardTypeCode,
          cardType: cardType?.value || '',
          brandOwnerCode: card?.brandOwnerTypeCode || '',
        });
        setSalaryVisible(!!data.isSalaryVisible);
        setCustomerTypes(data.parameterDataList.map(optionFormat));
      } else if (type === ProductComboType.CUSTOMERSUB) {
        setValue('customerType', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          const { data } = await Product.getProductCombo({
            productComboType: type,
            fillPointId: subPos?.value || '',
            customerTypeCode: item?.value,
            cardTypeCode: card?.cardTypeCode,
            cardType: cardType?.value || '',
            brandOwnerCode: card?.brandOwnerTypeCode || '',
          });
          setCustomerSubTypes(data.parameterDataList.map(optionFormat));
        } else {
          setCustomerSubTypes([]);
        }
      } else if (type === ProductComboType.DEBITFLAG) {
        setValue('customerSubType', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          const { data } = await Product.getProductCombo({
            productComboType: type,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: customerType?.value,
            customerSubTypeCode: item?.value,
            cardType: cardType?.value || '',
            brandOwnerCode: card?.brandOwnerTypeCode || '',
          });
          setDebitFlags(data.parameterDataList.map(optionFormat));
        } else {
          setDebitFlags([]);
        }
      } else if (type === ProductComboType.RESIDENT) {
        setValue('debitFlag', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          const { data } = await Product.getProductCombo({
            productComboType: type,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: customerType?.value,
            customerSubTypeCode: customerSubType?.value,
            debitFlagCode: item?.value,
          });
          setResidents(data.parameterDataList.map(optionFormat));
        } else {
          setResidents([]);
        }
      } else if (type === ProductComboType.ACCOUNT) {
        setValue('resident', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          const { data } = await Product.getProductCombo({
            productComboType: type,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: customerType?.value,
            customerSubTypeCode: customerSubType?.value,
            debitFlagCode: debitFlag?.value,
            residentCode: item?.value,
            brandOwnerCode: card?.brandOwnerTypeCode || '',
          });
          setAccountTypes(data.parameterDataList.map(optionFormat));
        } else {
          setAccountTypes([]);
        }
      } else if (type === ProductComboType.BRANDOWNER) {
        setValue('accountType', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          const { data } = await Product.getProductCombo({
            productComboType: type,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: customerType?.value,
            customerSubTypeCode: customerSubType?.value,
            debitFlagCode: debitFlag?.value,
            residentCode: resident?.value,
            accountTypeCode: item?.value,
            brandOwnerCode: card?.brandOwnerTypeCode || '',
          });
          setBrandOwners(data.parameterDataList.map(optionFormat));
        } else {
          setBrandOwners([]);
        }
      } else if (type === ProductComboType.CARD) {
        setValue('brandOwner', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          const { data } = await Product.getProductCombo({
            productComboType: type,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: customerType?.value,
            customerSubTypeCode: customerSubType?.value,
            debitFlagCode: debitFlag?.value,
            residentCode: resident?.value,
            accountTypeCode: accountType?.value,
            brandOwnerCode: item?.value,
          });
          setProductCardTypes(data.parameterDataList.map(optionFormat));
        } else {
          setProductCardTypes([]);
        }
      } else {
        setValue('productCardType', item, { shouldValidate: true, shouldDirty: true });
        if (item) {
          await Product.getProductLineDetails({
            customerTypeCode: customerType?.value || '',
            customerSubTypeCode: customerSubType?.value || '',
            debitFlagCode: debitFlag?.value || '',
            residentCode: resident?.value || '',
            accountTypeCode: accountType?.value || '',
            brandOwnerCode: brandOwner?.value || '',
            cardTypeCode: item?.value || '',
          });
        }
      }
    },
    [getValues, productDetailPRMSetters, setValue]
  );

  const loadFormFields = useCallback(async (): Promise<void> => {
    const { data } = await ProductPageValidation.getProductPageData();

    if (data.result === 'OK' && data.pageData) {
      setIsLoading(true);
      const {
        pos,
        subPos,
        cardType,
        cardSelectionType,
        mainCardInfo,
        cardImageId,
        printType,
        cardUsageType,
        customerType,
        customerSubType,
        debitFlag,
        resident,
        accountType,
        brandOwner,
        productCardType,
        isProductTransfer,
        isSignDocuments,
        transferableCardInfo,
        // TODO Update property name (isSalaryClient) after service is changed
        isSalaryVisible: isSalaryClient,
        isProductOfferChecked,
        isProductOfferVisible,
        isSalaryChecked,
      } = data.pageData;
      setProductOfferVisible(isProductOfferVisible);
      setProductOfferChecked(isProductOfferChecked);

      const cardList = await getCards(
        optionFormat(cardType),
        optionFormat(printType),
        optionFormat(subPos)
      );
      setSalaryVisible(isSalaryClient);
      getSubPointOfSales(optionFormat(pos));
      const card = cardList.find((crd) => crd.id === cardImageId) || null;

      [
        { name: 'pos', value: optionFormat(pos) },
        { name: 'subPos', value: optionFormat(subPos) },
        { name: 'printType', value: optionFormat(printType) },
        { name: 'cardType', value: optionFormat(cardType) },
        { name: 'cardUsageType', value: cardUsageType },
        { name: 'mainCard', value: mainCardInfo || null },
        { name: 'isProductTransfer', value: isProductTransfer },
        { name: 'isSignDocuments', value: isSignDocuments },
        { name: 'transferableCardInfo', value: transferableCardInfo || null },
        ...(isProductOfferChecked
          ? [{ name: 'isProductOffer', value: isProductOfferChecked }]
          : [
              { name: 'isProductOffer', value: isProductOfferChecked },
              { name: 'cardSelectionType', value: cardSelectionType },

              { name: 'card', value: card },
              { name: 'customerType', value: optionFormat(customerType) },
              { name: 'customerSubType', value: optionFormat(customerSubType) },
              { name: 'debitFlag', value: optionFormat(debitFlag) },
              { name: 'resident', value: optionFormat(resident) },
              { name: 'accountType', value: optionFormat(accountType) },
              { name: 'brandOwner', value: optionFormat(brandOwner) },
              { name: 'productCardType', value: optionFormat(productCardType) },
              { name: 'isSalaryClient', value: isSalaryChecked },
            ]),
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
      setIsLoading(false);
    }
  }, [getCards, setValue]);

  const loadAllProductCombos = useCallback(async (): Promise<void> => {
    const {
      customerType,
      customerSubType,
      debitFlag,
      resident,
      accountType,
      brandOwner,
      productCardType,
    } = getValues();
    setIsComboParametersLoading(true);
    await getProductComboItems(ProductComboType.CUSTOMER)(null);
    await getProductComboItems(ProductComboType.CUSTOMERSUB)(customerType);
    await getProductComboItems(ProductComboType.DEBITFLAG)(customerSubType);
    await getProductComboItems(ProductComboType.RESIDENT)(debitFlag);
    await getProductComboItems(ProductComboType.ACCOUNT)(resident);
    await getProductComboItems(ProductComboType.BRANDOWNER)(accountType);
    await getProductComboItems(ProductComboType.CARD)(brandOwner);
    await getProductComboItems()(productCardType);
    setIsLoading(false);
    setIsComboParametersLoading(false);
  }, [getProductComboItems, getValues]);

  useEffect(() => {
    if (isLoading && !isComboParameterLoading) {
      const {
        card,
        subPos,
        customerType,
        customerSubType,
        debitFlag,
        resident,
        accountType,
        brandOwner,
        productCardType,
      } = getValues();
      if (
        card &&
        subPos &&
        customerType &&
        customerSubType &&
        debitFlag &&
        resident &&
        accountType &&
        brandOwner &&
        productCardType
      ) {
        loadAllProductCombos();
      } else if (productOfferChecked) {
        setIsLoading(false);
        setIsComboParametersLoading(false);
      }
    }
  }, [isLoading, isComboParameterLoading, getValues, loadAllProductCombos, productOfferChecked]);

  const onSubmit = async (form: FormData): Promise<void> => {
    if (disablePage || isProductOfferFlow) {
      goToNextStep();
    } else {
      const { data } = await ProductPageValidation.validateProductPageData({
        pos: dictionaryLineFormat(form.pos),
        subPos: dictionaryLineFormat(form.subPos),
        cardType: dictionaryLineFormat(form.cardType),
        printType: dictionaryLineFormat(form.printType),
        cardSelectionType: form.cardSelectionType,
        cardUsageType: form.cardUsageType,
        ...(form.cardUsageType === CardUsageType.SUPPLEMENTARY
          ? { mainCardInfo: form.mainCard || undefined }
          : {}),
        cardImageId: form.card?.id,
        customerType: dictionaryLineFormat(form.customerType),
        customerSubType: dictionaryLineFormat(form.customerSubType),
        debitFlag: dictionaryLineFormat(form.debitFlag),
        resident: dictionaryLineFormat(form.resident),
        accountType: dictionaryLineFormat(form.accountType),
        brandOwner: dictionaryLineFormat(form.brandOwner),
        productCardType: dictionaryLineFormat(form.productCardType),
        isSalaryChecked: form.isSalaryClient,
        isSalaryVisible,
        pageOperationType: isEdit ? PageOperationType.EDIT : PageOperationType.NEW,
        isProductTransfer: form.isProductTransfer,
        ...(form.isProductTransfer
          ? { transferableCardInfo: form.transferableCardInfo || undefined }
          : {}),
        isSignDocuments: form.isSignDocuments,
        isProductOfferChecked: form.isProductOffer,
        isProductOfferVisible: productOfferVisible,
      });
      if (data.result === 'OK') {
        dispatchCardType(
          form.cardType?.value === 'I' ? CardSelectionType.CREDITCARD : CardSelectionType.DEBIT
        );
        dispatchProductOffer(form.isProductOffer);
        goToNextStep();
      }
    }
  };

  useEffect(() => scrollTo(0, 0), [errors]);

  useEffect(() => {
    (async () => {
      const { data } = await Product.getPOSData();
      setMainPointOfSales(data.posDataList.map(optionFormat));
    })();
    loadFormFields();
  }, [loadFormFields]);

  useEffect(() => {
    const { isProductOffer } = getValues();
    register({ name: 'pos' });
    register({ name: 'subPos' });
    register({ name: 'cardType' });
    register({ name: 'cardUsageType' });
    register({ name: 'printType' });

    if (!isProductOffer) {
      register({ name: 'card' });
      register({ name: 'customerType' });
      register({ name: 'customerSubType' });
      register({ name: 'debitFlag' });
      register({ name: 'resident' });
      register({ name: 'accountType' });
      register({ name: 'brandOwner' });
      register({ name: 'productCardType' });
      register({ name: 'isSalaryClient' });
      register({ name: 'mainCard' });
    }

    register({ name: 'isProductTransfer' });
    register({ name: 'isSignDocuments' });
    register({ name: 'isProductOffer' });
    register({ name: 'transferableCardInfo' });
  }, [getValues, register]);

  const getSubPointOfSales = async (posItem: Option | null): Promise<void> => {
    if (posItem) {
      const subPosResponse = await Parameter.getReferenceData({
        referenceDataType: ReferenceDataType.CARDFILLPOINT,
        additionalCode: posItem?.value || '',
      });
      setSubPointOfSales(subPosResponse.data.parameterDataList?.map(optionFormat) || []);
    } else {
      setSubPointOfSales([]);
    }
  };

  const handlePosChange = async (posItem: Option | null): Promise<void> => {
    setSubPointOfSales([]);
    [
      { name: 'pos', value: posItem },
      { name: 'subPos', value: null },
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    if (posItem) {
      const {
        data: { cardSelectionType },
      } = await Product.getPosDataDetails(posItem.value);

      getSubPointOfSales(posItem);

      [
        { name: 'cardSelectionType', value: cardSelectionType },
        { name: 'cardType', value: null },
        { name: 'mainCard', value: null },
        { name: 'card', value: null },
        { name: 'customerType', value: null },
        { name: 'customerSubType', value: null },
        { name: 'debitFlag', value: null },
        { name: 'resident', value: null },
        { name: 'accountType', value: null },
        { name: 'brandOwner', value: null },
        { name: 'productCardType', value: null },
        { name: 'isProductTransfer', value: false },
        { name: 'isSignDocuments', value: false },
        { name: 'isProductOffer', value: false },
        { name: 'transferableCardInfo', value: null },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
      if (cardSelectionType === CardSelectionType.DEFAULT) return;
      const [creditCard, debitCard] = cardTypes;
      const cardType = cardSelectionType === CardSelectionType.DEBIT ? debitCard : creditCard;
      setValue('cardType', cardType);
    }
  };

  const handleSubPosChange = (subPosItem: Option | null): void => {
    const { cardSelectionType, cardType, printType } = getValues();
    [
      { name: 'subPos', value: subPosItem },
      { name: 'mainCard', value: null },
      { name: 'card', value: null },
      { name: 'customerType', value: null },
      { name: 'customerSubType', value: null },
      { name: 'debitFlag', value: null },
      { name: 'resident', value: null },
      { name: 'accountType', value: null },
      { name: 'brandOwner', value: null },
      { name: 'productCardType', value: null },
      { name: 'isProductTransfer', value: false },
      { name: 'isSignDocuments', value: false },
      { name: 'isProductOffer', value: false },
      { name: 'transferableCardInfo', value: null },
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

    setProductOfferVisible(!!subPosItem);

    if (cardSelectionType === CardSelectionType.DEFAULT) {
      setValue('cardType', null);
    } else if (cardType && printType) {
      getCards(cardType, printType, subPosItem);
    }
  };

  const handleCardTypeChange = async (cardType: Option | null): Promise<void> => {
    const { printType, subPos } = getValues();
    [
      { name: 'cardType', value: cardType },
      { name: 'mainCard', value: null },
      { name: 'card', value: null },
      { name: 'customerType', value: null },
      { name: 'customerSubType', value: null },
      { name: 'debitFlag', value: null },
      { name: 'resident', value: null },
      { name: 'accountType', value: null },
      { name: 'brandOwner', value: null },
      { name: 'productCardType', value: null },
      { name: 'isProductTransfer', value: false },
      { name: 'isSignDocuments', value: false },
      { name: 'isProductOffer', value: false },
      { name: 'transferableCardInfo', value: null },
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    if (printType && subPos) {
      getCards(cardType, printType, subPos);
    }
  };

  const handePrintTypeChange = async (printType: Option | null): Promise<void> => {
    const { cardType, subPos } = getValues();

    [
      { name: 'printType', value: printType },
      { name: 'mainCard', value: null },
      { name: 'card', value: null },
      { name: 'customerType', value: null },
      { name: 'customerSubType', value: null },
      { name: 'debitFlag', value: null },
      { name: 'resident', value: null },
      { name: 'accountType', value: null },
      { name: 'brandOwner', value: null },
      { name: 'productCardType', value: null },
      { name: 'isProductTransfer', value: false },
      { name: 'isSignDocuments', value: false },
      { name: 'transferableCardInfo', value: null },
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    if (cardType && subPos) {
      getCards(cardType, printType, subPos);
    }
  };

  const handleCardUsageChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('cardUsageType', target.value);
  };

  const handleMainCardSelect = (
    card: CardListData | TransferableCardData,
    formValues: SearchFormData
  ): void => {
    setShowCardModal(false);
    if (watch('isProductTransfer')) {
      setValue('transferableCardInfo', card as TransferableCardData, { shouldValidate: true });
    } else {
      setValue('mainCard', card as CardListData, { shouldValidate: true });
    }

    setCardModalFormValues(formValues);
  };

  const handleCardChange = (card: CardComboModalItem): void => {
    setValue('card', card);
    getProductComboItems(ProductComboType.CUSTOMER)(null);
    setValue('isSalaryClient', false);
  };

  const handleProductTransferChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('isProductTransfer', target.checked);
    setValue('cardUsageType', CardUsageType.PRIMARY);
  };

  const handleIsSignDocumentsChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('isSignDocuments', target.checked);
  };

  useEffect(() => {
    const getProductOfferState = async (): Promise<void> => {
      const { data } = await ProductOffer.getProductOfferVisibility({
        isProductOfferChecked: productOfferState,
      });
      if (data.result === 'OK') {
        setValue('cardType', optionFormat(data.parameterDataList[0]) || null);
        setCardTypeUsageEnabled(data.isCardUsageTypeEnabled);
        setCardTypeVisible(data.isCardTypeVisible);
      }
    };
    getProductOfferState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProductOfferChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('isProductOffer', target.checked);
    setValue('cardUsageType', CardUsageType.PRIMARY);
    const { data } = await ProductOffer.getProductOfferVisibility({
      isProductOfferChecked: target.checked,
    });
    if (data.result === 'OK') {
      setValue('cardType', optionFormat(data.parameterDataList[0]) || null);
      setCardTypeUsageEnabled(data.isCardUsageTypeEnabled);
      setCardTypeVisible(data.isCardTypeVisible);

      setValue('printType', cardPrintTypes.find((card) => card.value === '1') || null);
      await handePrintTypeChange(
        cardPrintTypes.find((card) => card.value === '1') || { label: '', value: '' }
      );
      setValue('isProductTransfer', false);
    }
  };
  const handleSalaryClientChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('isSalaryClient', target.checked);
    if (target.checked) {
      const { data } = await Product.getProductSalaryLine({});
      if (data.result === 'OK') {
        const { subPos, card } = getValues();

        const promiseArray = await Promise.all([
          Product.getProductCombo({
            productComboType: ProductComboType.CUSTOMERSUB,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: data.customerTypeCode,
          }),
          Product.getProductCombo({
            productComboType: ProductComboType.DEBITFLAG,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: data.customerTypeCode,
            customerSubTypeCode: data.customerSubTypeCode,
          }),
          Product.getProductCombo({
            productComboType: ProductComboType.RESIDENT,
            fillPointId: subPos?.value || '',
            cardTypeCode: card?.cardTypeCode,
            customerTypeCode: data.customerTypeCode,
            customerSubTypeCode: data.customerSubTypeCode,
            debitFlagCode: data.debitFlagCode,
          }),
        ]);

        const [customerSubTypeList, debitFlagList, residentList] = promiseArray.map((promise) =>
          promise.data.parameterDataList.map(optionFormat)
        );

        const customerType =
          customerTypes.find((item) => item.value === data.customerTypeCode) || null;
        const customerSubType =
          customerSubTypeList.find((item) => item.value === data.customerSubTypeCode) || null;
        const debitFlag = debitFlagList.find((item) => item.value === data.debitFlagCode) || null;

        setValue('customerType', customerType, { shouldValidate: true, shouldDirty: true });
        setValue('customerSubType', customerSubType, { shouldValidate: true, shouldDirty: true });
        setValue('debitFlag', debitFlag, { shouldValidate: true, shouldDirty: true });

        setCustomerSubTypes(customerSubTypeList);
        setDebitFlags(debitFlagList);
        setResidents(residentList);
      }
    }
  };

  return (
    <Container>
      {showCardModal ? (
        <MainCardSelect
          cardSelectionType={
            watch('cardType')?.value === 'I'
              ? CardSelectionType.CREDITCARD
              : CardSelectionType.DEBIT
          }
          formValues={cardModalFormValues}
          isProductTransfer={watch('isProductTransfer')}
          onCardSelect={handleMainCardSelect}
          onClose={toggleModal}
        />
      ) : null}
      <Title>
        <Trans i18nKey="cardEntry_productInfo_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_productInfo_pointOfSale_text" />
      </Subtitle>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <Loader /> : null}
        <FormRow>
          <FormElement>
            <Combobox
              disabled={isDisabled}
              errorMessage={t(errors?.pos?.message as string)}
              items={mainPointOfSales}
              label={t('cardEntry_productInfo_pointOfIssue_label')}
              name="pos"
              onChange={handlePosChange}
              value={watch('pos')}
              required
            />
          </FormElement>
          <FormElement>
            <Combobox
              disabled={isDisabled}
              errorMessage={t(errors?.subPos?.message as string)}
              items={subPointOfSales}
              label={t('cardEntry_productInfo_fillPoint_label')}
              name="subPos"
              onChange={handleSubPosChange}
              value={watch('subPos')}
              required
            />
          </FormElement>
        </FormRow>

        {watch('subPos') ? (
          <>
            {productOfferVisible ? (
              <FormRow>
                <CheckboxContainer>
                  <Checkbox
                    disabled={isDisabled}
                    id="isProductOffer"
                    label={t('cardEntry_productInfo_productOffer_productOffer_label')}
                    onChange={handleProductOfferChange}
                    value={watch('isProductOffer')}
                  />
                  <TooltipContainer>
                    <Tooltip
                      content={
                        <Markdown
                          text={t('cardEntry_productInfo_productOffer_productOfferTooltip_text')}
                        />
                      }
                    >
                      <img alt="tooltip" src={infoIcon} />
                    </Tooltip>
                  </TooltipContainer>
                </CheckboxContainer>
              </FormRow>
            ) : null}
            <Subtitle>
              <Trans i18nKey="cardEntry_stepper_cardStep_title" />
            </Subtitle>
            <FormRow>
              {cardTypeVisible ? (
                <FormElement>
                  <Combobox
                    disabled={isDisabled}
                    errorMessage={t(errors?.cardType?.message as string)}
                    items={
                      watch('cardSelectionType') === CardSelectionType.DEFAULT ? cardTypes : []
                    }
                    label={t('cardEntry_productInfo_cardType_label')}
                    name="cardType"
                    onChange={handleCardTypeChange}
                    value={watch('cardType')}
                    required
                  />
                </FormElement>
              ) : null}
              <FormElement>
                <Combobox
                  disabled={isDisabled}
                  errorMessage={t(errors?.printType?.message as string)}
                  items={cardPrintTypes}
                  label={t('cardEntry_productInfo_cardPrintType_label')}
                  name="printType"
                  onChange={handePrintTypeChange}
                  value={watch('printType')}
                  required
                />
              </FormElement>
            </FormRow>
            {watch('cardType') && watch('printType') ? (
              <>
                {watch('cardType')?.value === 'I' && !watch('isProductOffer') ? (
                  <FormRow>
                    <CheckboxContainer>
                      <Checkbox
                        disabled={isDisabled}
                        id="isProductTransfer"
                        label={t('cardEntry_productInfo_productTransfer_productTransfer_label')}
                        onChange={handleProductTransferChange}
                        value={watch('isProductTransfer')}
                      />
                    </CheckboxContainer>
                    {watch('isProductTransfer') ? (
                      <MainCardSelector>
                        <MainCardInput
                          errorMessage={t(errors?.transferableCardInfo?.message as string)}
                          label={t('cardEntry_productInfo_productTransfer_sourceCard_label')}
                          name="transferableCardInfo"
                          value={watch('transferableCardInfo')?.transferableCardNumber || ''}
                          disabled
                          required
                        />
                        <MainCardButton
                          color={ButtonColor.Primary}
                          disabled={isDisabled}
                          onClick={toggleModal}
                          size={ButtonSize.Medium}
                          text={t(
                            watch('transferableCardInfo')?.transferableCardNumber
                              ? 'general_change_button'
                              : 'general_select_button'
                          )}
                          type="button"
                        />
                      </MainCardSelector>
                    ) : null}
                  </FormRow>
                ) : null}
                {watch('cardType')?.value === 'I' ? (
                  <FormRow>
                    <CheckboxElement>
                      <Checkbox
                        disabled={isDisabled}
                        id="isSignDocuments"
                        label={t('cardEntry_productInfo_signDocuments_label')}
                        onChange={handleIsSignDocumentsChange}
                        value={watch('isSignDocuments')}
                      />
                    </CheckboxElement>
                  </FormRow>
                ) : null}

                <Separator />
                <Subtitle>
                  <Trans i18nKey="cardEntry_productInfo_cardUsageType_label" />
                </Subtitle>
                <FormRow>
                  <RadioButtonContainer>
                    <RadioButtonGroup
                      disabled={
                        watch('isProductTransfer') ||
                        disablePage ||
                        isProductOfferFlow ||
                        !cardTypeUsageEnabled
                      }
                      onChange={handleCardUsageChange}
                      value={watch('cardUsageType')}
                    >
                      <StyledRadioButton
                        label={t('cardEntry_productInfo_primary_label')}
                        name={CardUsageType.PRIMARY}
                      />
                      <StyledRadioButton
                        label={t('cardEntry_productInfo_supplementary_label')}
                        name={CardUsageType.SUPPLEMENTARY}
                      />
                    </RadioButtonGroup>
                  </RadioButtonContainer>
                  {watch('cardUsageType') === CardUsageType.SUPPLEMENTARY ? (
                    <MainCardSelector>
                      <MainCardInput
                        errorMessage={t(errors?.mainCard?.message as string)}
                        label={t('cardEntry_productInfo_selectMainCard_label')}
                        name="mainCard"
                        value={watch('mainCard')?.cardNumber || ''}
                        disabled
                        required
                      />
                      <MainCardButton
                        color={ButtonColor.Primary}
                        onClick={toggleModal}
                        size={ButtonSize.Medium}
                        text={t(
                          watch('mainCard')?.cardNumber
                            ? 'general_change_button'
                            : 'general_select_button'
                        )}
                        type="button"
                      />
                    </MainCardSelector>
                  ) : null}
                </FormRow>

                {watch('isProductOffer') ? (
                  <FormRow>
                    <Button
                      color={ButtonColor.Primary}
                      disabled={formState.isSubmitting}
                      size={ButtonSize.Medium}
                      text={t('general_continue_button')}
                      type="submit"
                    />
                  </FormRow>
                ) : (
                  <>
                    <FormRow>
                      {cards.length > 0 ? (
                        <LineFormElement>
                          <ComboModal
                            closeText={t('general_components_close_button')}
                            emptyItemMessage={t('general_searchNoResult_errorMessage')}
                            errorMessage={t(errors?.card?.message as string)}
                            initialItemCount={isTablet ? 3 : 4}
                            items={cards}
                            label={t('cardEntry_productInfo_cardSelection_label')}
                            moreText={t('general_more_button')}
                            onChange={handleCardChange as (card: ComboModalItem) => void}
                            value={watch('card')}
                          />
                        </LineFormElement>
                      ) : (
                        <NoCardText>
                          <Trans i18nKey="cardEntry_productInfo_noCard_errorMessage" />
                        </NoCardText>
                      )}
                    </FormRow>
                    {watch('card') ? (
                      <>
                        {isSalaryVisible && !isEdit ? (
                          <FormRow>
                            <FormElement>
                              <Checkbox
                                id="isSalaryClient"
                                label={t('cardEntry_salaryClient_label')}
                                onChange={handleSalaryClientChange}
                                value={watch('isSalaryClient')}
                              />
                            </FormElement>
                          </FormRow>
                        ) : null}
                        <Subtitle>
                          <Trans i18nKey="cardEntry_productInfo_productDetail_text" />
                        </Subtitle>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              disabled={watch('isSalaryClient')}
                              errorMessage={t(errors?.customerType?.message as string)}
                              items={customerTypes}
                              label={t('cardEntry_productInfo_customerType_label')}
                              name="customerType"
                              onChange={getProductComboItems(ProductComboType.CUSTOMERSUB)}
                              value={watch('customerType')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              disabled={watch('isSalaryClient')}
                              errorMessage={t(errors?.customerSubType?.message as string)}
                              items={customerSubTypes}
                              label={t('cardEntry_productInfo_customerSubType_label')}
                              name="customerSubType"
                              onChange={getProductComboItems(ProductComboType.DEBITFLAG)}
                              value={watch('customerSubType')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              disabled={watch('isSalaryClient')}
                              errorMessage={t(errors?.debitFlag?.message as string)}
                              items={debitFlags}
                              label={t('cardEntry_productInfo_debit_label')}
                              name="debitFlag"
                              onChange={getProductComboItems(ProductComboType.RESIDENT)}
                              value={watch('debitFlag')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              errorMessage={t(errors?.resident?.message as string)}
                              items={residents}
                              label={t('cardEntry_productInfo_resident_label')}
                              name="resident"
                              onChange={getProductComboItems(ProductComboType.ACCOUNT)}
                              value={watch('resident')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              errorMessage={t(errors?.accountType?.message as string)}
                              items={accountTypes}
                              label={t('cardEntry_productInfo_accountType_label')}
                              name="accountType"
                              onChange={getProductComboItems(ProductComboType.BRANDOWNER)}
                              value={watch('accountType')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              errorMessage={t(errors?.brandOwner?.message as string)}
                              items={brandOwners}
                              label={t('cardEntry_productInfo_brandOwner_label')}
                              name="brandOwner"
                              onChange={getProductComboItems(ProductComboType.CARD)}
                              value={watch('brandOwner')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <FormRow>
                          <FormElement>
                            <Combobox
                              errorMessage={t(errors?.productCardType?.message as string)}
                              items={productCardTypes}
                              label={t('cardEntry_productInfo_productCardType_label')}
                              name="productCardType"
                              onChange={getProductComboItems()}
                              value={watch('productCardType')}
                              required
                            />
                          </FormElement>
                        </FormRow>
                        <Button
                          color={ButtonColor.Primary}
                          disabled={formState.isSubmitting}
                          size={ButtonSize.Medium}
                          text={t('general_continue_button')}
                          type="submit"
                        />
                      </>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </Form>
    </Container>
  );
};

export default ProductInformation;
